import moment from "moment";
import { useTranslation } from "react-i18next";
import Button from "src/components/Button";
import FlatList from "src/components/FlatList";
import Link from "src/components/Link";
import ListProps from "src/components/lists/ListProps";
import PaymentBadge from "src/components/payment/PaymentBadge";
import Table from "src/components/Table";
import useGet from "src/hooks/rest/useGet";
import useAuth from "src/hooks/selectors/useAuth";
import useWindowSize from "src/hooks/useWindowSize";
import { formatPrice } from "src/lib/formatters";

const PaymentList = ({ query }: ListProps) => {
	const { t } = useTranslation(["lists", "payments"]);
	const auth = useAuth();
	const { isPhone } = useWindowSize();
	const [result, loading, { fetchNext, handleSearch }] = useGet(
		`/${auth.type}/payments?${query || ""}`,
		{ data: [] },
		true
	);

	const { data, hasMore, meta } = result;

	const translations = {
		status: t("status", { ns: "lists", keyPrefix: "headers" }),
		invoice_number: t("invoice-number", {
			ns: "lists",
			keyPrefix: "headers",
		}),
		method: t("method", { ns: "lists", keyPrefix: "headers" }),
		type: t("type", { ns: "lists", keyPrefix: "headers" }),
		price: t("price", { ns: "lists", keyPrefix: "headers" }),
		description: t("description", { ns: "lists", keyPrefix: "headers" }),
		company: t("company", { ns: "lists", keyPrefix: "headers" }),
		task: t("task", { ns: "lists", keyPrefix: "headers" }),
		date: t("estimated-date", { ns: "lists", keyPrefix: "headers" }),
	};

	let headers = [] as string[];
	if (auth.type === "company") {
		headers = [
			translations.status,
			translations.invoice_number,
			translations.method,
			translations.type,
			translations.price,
			translations.description,
			"",
		];
	} else {
		headers = [
			translations.status,
			translations.task,
			translations.company,
			translations.price,
			translations.date,
		];
	}

	if (isPhone) {
		headers.splice(headers.indexOf(translations.method), 1);
		headers.splice(headers.indexOf(translations.status), 1);
		headers.splice(headers.indexOf(translations.description), 1);
		headers.splice(headers.indexOf(translations.company), 1);
	}

	const formatTotalTitle = (total: number) => {
		return t("total", {
			count: total,
			keyPrefix: "payment-list",
			ns: "lists",
		});
	};

	return (
		<FlatList
			variant="flat"
			{...{ loading, hasMore, handleSearch, meta, formatTotalTitle }}
			data={data || []}
			onNext={async () => {
				if (hasMore) await fetchNext();
			}}
			table={{
				headers,
			}}
			empty={{
				title: t("empty.title", {
					keyPrefix: "payment-list",
					ns: "lists",
				}),
			}}
			renderItem={(row: any) => (
				<>
					{isPhone && auth.type === "student" && (
						<Table.Row
							className={
								row.needs_attention
									? "bg-red-200 border-0"
									: "border-0"
							}
							key={row.id + row.hid}
						>
							<Table.Cell className="min-w-[180px]">
								<div className="flex">
									<PaymentBadge status={row.status} />
								</div>
							</Table.Cell>
							<Table.Cell>
								{formatPrice(row.total - 1)}
							</Table.Cell>
						</Table.Row>
					)}

					<Table.Row
						className={row.needs_attention ? "bg-red-200" : ""}
						key={row.id + row.hid}
					>
						{!isPhone && (
							<Table.Cell className="min-w-[180px]">
								<div className="flex">
									<PaymentBadge status={row.status} />
								</div>
							</Table.Cell>
						)}
						{headers.includes(translations.task) && row.task && (
							<Table.Cell
								colSpan={isPhone ? 2 : undefined}
								className="w-full"
							>
								{row?.task?.hid && row?.registration?.hid ? (
									<Link
										to={`/tasks/${row?.task?.hid}/show/${row?.registration?.hid}`}
										target="_blank"
										className="underline"
									>
										{row?.task?.title}
										<small>
											<i className="fal fa-external-link ml-2"></i>
										</small>
									</Link>
								) : (
									<span>{row?.task?.title}</span>
								)}
							</Table.Cell>
						)}
						{headers.includes(translations.invoice_number) && (
							<Table.Cell>{row.invoice_number}</Table.Cell>
						)}
						{headers.includes(translations.method) && (
							<Table.Cell>
								{t(`methods.${row.type}`, {
									ns: "payments",
								})}
							</Table.Cell>
						)}
						{headers.includes(translations.type) && (
							<Table.Cell>
								{["INCREASE_WALLET", "SUBSCRIPTION"].includes(
									row.reference
								)
									? t(`types.subscription`, {
											ns: "payments",
									  })
									: t(`types.payout`, {
											ns: "payments",
									  })}
							</Table.Cell>
						)}
						{auth.type === "company" && isPhone && (
							<Table.Cell>{formatPrice(row.total)}</Table.Cell>
						)}
						{!isPhone && (
							<>
								{headers.includes(translations.company) && (
									<Table.Cell className="w-full">
										<p className="whitespace-nowrap">
											{row?.company?.name}
										</p>
									</Table.Cell>
								)}
								<Table.Cell>
									{formatPrice(
										row.total -
											(auth.type === "student" ? 1 : 0)
									)}
								</Table.Cell>
								{headers.includes(translations.description) && (
									<Table.Cell>{row.description}</Table.Cell>
								)}
							</>
						)}
						{auth.type === "student" && (
							<Table.Cell>
								{row.status === "completed"
									? moment(row.updated_at).format(
											"DD-MM-YYYY"
									  )
									: "-"}
							</Table.Cell>
						)}
						{auth.type === "company" && (
							<Table.Cell>
								<Button
									iconOnly
									type="gray"
									to={`/payments/${row.hid}`}
								>
									<i className="fas fa-eye"></i>
								</Button>
							</Table.Cell>
						)}
					</Table.Row>
				</>
			)}
		/>
	);
};

export default PaymentList;
